import { useEffect, useState } from 'react';
import { CheckCircle, AlertCircle, Users } from 'lucide-react';
import { adminLookupAPI } from '../../services/api';

/**
 * Company → Co-managed IT card.
 *
 * The client's own IT team works their company's tickets in the portal. This
 * decides where those tickets live (a board of the company's own, so the
 * MSP's alert routes never see them until escalation), which statuses the
 * team may set, and where "Escalate" sends a ticket. Every status is picked
 * from the board it belongs to, because ConnectWise refuses one from
 * another board and the server checks that too.
 *
 * Who the technicians are is not set here: it is whoever holds a role with
 * "Work the queue" at this company (IT Technician, Company Admin, or a
 * custom role) — Admin → Users, or the company's own Team page.
 */

const inputClass = 'w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

const empty = () => ({
  enabled: true,
  boardId: '',
  newStatusId: '',
  allowedStatusIds: [],
  closedStatusId: '',
  reopenStatusId: '',
  escalationBoardId: '',
  escalationStatusId: '',
  notifyTeamOnNew: true,
  notifyRequesterOnReply: true,
});

export default function CompanyComanagedSection({ data, loading, onSave, onReset }) {
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(empty());
  const [errors, setErrors] = useState([]);
  const [boards, setBoards] = useState([]);
  const [statuses, setStatuses] = useState({}); // boardId -> [{id, name, closedStatus}]

  const settings = data?.settings || null;
  const technicians = data?.technicians || [];
  const entitled = data?.entitled !== false;

  useEffect(() => {
    if (!editMode || boards.length) return;
    adminLookupAPI.boards().then((r) => setBoards(r.data.items || [])).catch(() => setBoards([]));
  }, [editMode, boards.length]);

  const loadStatuses = async (boardId) => {
    const id = Number(boardId);
    if (!id || statuses[id]) return;
    try {
      const r = await adminLookupAPI.boardStatuses(id);
      setStatuses((p) => ({ ...p, [id]: r.data.items || [] }));
    } catch {
      setStatuses((p) => ({ ...p, [id]: [] }));
    }
  };
  useEffect(() => {
    if (!editMode) return;
    if (form.boardId) loadStatuses(form.boardId);
    if (form.escalationBoardId) loadStatuses(form.escalationBoardId);
  }, [editMode, form.boardId, form.escalationBoardId]);

  const startEdit = () => {
    const next = empty();
    if (settings) {
      for (const k of Object.keys(next)) next[k] = settings[k] ?? next[k];
      for (const k of ['boardId', 'newStatusId', 'closedStatusId', 'reopenStatusId', 'escalationBoardId', 'escalationStatusId']) {
        next[k] = settings[k] ? String(settings[k]) : '';
      }
    }
    setForm(next);
    setErrors([]);
    setEditMode(true);
  };

  const save = async () => {
    setErrors([]);
    const num = (v) => (v === '' || v == null ? null : Number(v));
    const payload = {
      enabled: form.enabled,
      boardId: num(form.boardId),
      newStatusId: num(form.newStatusId),
      allowedStatusIds: form.allowedStatusIds.map(Number),
      closedStatusId: num(form.closedStatusId),
      reopenStatusId: num(form.reopenStatusId),
      escalationBoardId: num(form.escalationBoardId),
      escalationStatusId: num(form.escalationStatusId),
      notifyTeamOnNew: form.notifyTeamOnNew,
      notifyRequesterOnReply: form.notifyRequesterOnReply,
    };
    try {
      await onSave(payload);
      setEditMode(false);
    } catch (err) {
      const list = err.response?.data?.errors;
      setErrors(Array.isArray(list) && list.length ? list : [err.response?.data?.error || 'Failed to save']);
    }
  };

  const reset = async () => {
    if (!window.confirm('Stop co-managing this company? Its new tickets go to the default board again and its technicians lose the queue. Nothing in ConnectWise changes.')) return;
    setErrors([]);
    try {
      await onReset();
      setEditMode(false);
    } catch (err) {
      setErrors([err.response?.data?.error || 'Failed to remove']);
    }
  };

  const boardName = (id) => boards.find((b) => b.id === Number(id))?.name || (id ? `Board ${id}` : '—');
  const statusName = (boardId, id) => (statuses[Number(boardId)] || []).find((s) => s.id === Number(id))?.name || (id ? `Status ${id}` : '—');
  const intakeStatuses = statuses[Number(form.boardId)] || [];
  const escalationStatuses = statuses[Number(form.escalationBoardId)] || [];

  const select = (label, key, options, { hint, required } = {}) => (
    <div>
      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">{label}{required ? ' *' : ''}</label>
      <select value={form[key]} onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))} className={inputClass}>
        <option value="">{required ? 'Choose…' : 'None'}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>
      {hint && <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{hint}</p>}
    </div>
  );

  const toggle = (label, key, hint) => (
    <label className="flex items-start gap-3 cursor-pointer">
      <input type="checkbox" checked={form[key]} onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.checked }))} className="mt-1 rounded border-neutral-300" />
      <span>
        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{label}</span>
        {hint && <span className="block text-xs text-neutral-500 dark:text-neutral-400">{hint}</span>}
      </span>
    </label>
  );

  if (!entitled) {
    return (
      <div className="p-4 text-sm text-neutral-600 dark:text-neutral-300">
        Co-managed IT is not included in your plan. Contact your platform operator to add it.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {errors.length > 0 && (
        <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
          <ul className="list-disc pl-5 space-y-1">{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
      )}

      {!editMode && (
        <div className={`flex items-start justify-between gap-4 p-4 rounded-lg border ${settings?.enabled ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-neutral-50 dark:bg-neutral-700/50 border-neutral-200 dark:border-neutral-700'}`}>
          <div className="flex items-start gap-3">
            {settings?.enabled ? <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" /> : <AlertCircle className="w-5 h-5 text-neutral-400 mt-0.5" />}
            <div className="text-sm">
              {settings ? (
                <>
                  <p className="font-medium text-neutral-900 dark:text-white">{settings.enabled ? 'Co-managed' : 'Configured, switched off'}</p>
                  <p className="text-neutral-600 dark:text-neutral-300 mt-1">
                    New tickets go to board {settings.boardId} (status {settings.newStatusId}); escalation moves them to board {settings.escalationBoardId} (status {settings.escalationStatusId}).
                    {settings.allowedStatusIds?.length ? ` The team may set ${settings.allowedStatusIds.length} status${settings.allowedStatusIds.length === 1 ? '' : 'es'}.` : ' The team may set any open status on the board.'}
                  </p>
                </>
              ) : (
                <>
                  <p className="font-medium text-neutral-900 dark:text-white">Not co-managed</p>
                  <p className="text-neutral-600 dark:text-neutral-300 mt-1">This company&apos;s tickets go to the default board and its users cannot work the queue.</p>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {settings && (
              <button type="button" onClick={reset} disabled={loading} className="px-3 py-1.5 text-sm text-neutral-600 dark:text-neutral-300 hover:text-red-600 dark:hover:text-red-400">
                Stop co-managing
              </button>
            )}
            <button type="button" onClick={startEdit} disabled={loading} className="px-3 py-1.5 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50">
              {settings ? 'Edit' : 'Set up'}
            </button>
          </div>
        </div>
      )}

      {editMode && (
        <div className="space-y-4 p-4 rounded-lg border border-neutral-200 dark:border-neutral-700">
          {toggle('Co-managed', 'enabled', 'Off keeps the settings but sends new tickets to the default board and closes the queue.')}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {select('Intake board', 'boardId', boards, { required: true, hint: "A board of this company's own, so your alert routes never see their tickets until escalation." })}
            {select('New-ticket status', 'newStatusId', intakeStatuses, { required: true })}
            {select('Closed status', 'closedStatusId', intakeStatuses.filter((s) => s.closedStatus), { hint: 'What "Close" sets on this board. Blank: the portal default.' })}
            {select('Reopen status', 'reopenStatusId', intakeStatuses, { hint: 'When a requester replies to a closed ticket. Blank: the new-ticket status.' })}
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Statuses the team may set</label>
            <div className="flex flex-wrap gap-2">
              {intakeStatuses.filter((s) => !s.closedStatus).map((s) => {
                const on = form.allowedStatusIds.map(Number).includes(s.id);
                return (
                  <button
                    type="button"
                    key={s.id}
                    onClick={() => setForm((p) => ({ ...p, allowedStatusIds: on ? p.allowedStatusIds.filter((x) => Number(x) !== s.id) : [...p.allowedStatusIds, s.id] }))}
                    className={`px-2.5 py-1 text-xs rounded-full border ${on ? 'bg-primary-100 dark:bg-primary-900/40 border-primary-400 text-primary-700 dark:text-primary-300' : 'border-neutral-300 dark:border-neutral-600 text-neutral-600 dark:text-neutral-300'}`}
                  >
                    {s.name}
                  </button>
                );
              })}
              {!form.boardId && <span className="text-xs text-neutral-500">Choose the intake board first.</span>}
            </div>
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">None selected: every open status on the board.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {select('Escalation board', 'escalationBoardId', boards.filter((b) => String(b.id) !== String(form.boardId)), { required: true, hint: 'Your own board; the card in your chat says who escalated and why.' })}
            {select('Escalation status', 'escalationStatusId', escalationStatuses, { required: true })}
          </div>
          <div className="space-y-2">
            {toggle('Email the team about new tickets', 'notifyTeamOnNew', 'Every technician of this company, for tickets raised in the portal or handed back by you.')}
            {toggle('Email the requester when a technician replies', 'notifyRequesterOnReply', "From the board's connector address, so replies come back into ConnectWise.")}
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setEditMode(false)} className="px-3 py-1.5 text-sm text-neutral-600 dark:text-neutral-300">Cancel</button>
            <button type="button" onClick={save} disabled={loading} className="px-3 py-1.5 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50">
              {loading ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      <div>
        <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 flex items-center gap-2 mb-2">
          <Users className="w-4 h-4" /> Technicians ({technicians.length})
        </h4>
        {technicians.length === 0 ? (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Nobody yet. Give a user the IT Technician role at this company (Admin → Users → Company Access, or the company&apos;s Team page).
          </p>
        ) : (
          <ul className="text-sm text-neutral-700 dark:text-neutral-300 space-y-1">
            {technicians.map((t) => (
              <li key={t.id} className="flex items-center gap-2">
                <span className="font-medium">{t.name}</span>
                <span className="text-neutral-500 dark:text-neutral-400">{t.email}</span>
                <span className="text-xs px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-700">{String(t.role).replace(/_/g, ' ')}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {!editMode && settings && boards.length === 0 && (
        <p className="text-xs text-neutral-400">{boardName(settings.boardId)} · {statusName(settings.boardId, settings.newStatusId)}</p>
      )}
    </div>
  );
}
